import { useTranslation } from 'next-i18next'

import { useRouter } from 'next/router'

import { getVideoProgressData } from '~/statistics/utils/get-video-progress-data.utils'
import statisticsAnalytics from '~/analytics/statistics/statisticsAnalytics'
import { AppLanguage } from '~/types/locale'
import { ButtonPrimary } from '~/components/Buttons'
import { MathEssentialActivityStat, MathEssentialProductAPIStat } from '~/redux/apis/user-stats-api'

import ROUTES from '~/shared/constants/routes.const'

import PieProgress from '../PieProgress'

import ActivitySummary from './ActivitySummary'
import QuizStat from './QuizStat'
import ArticleStat from './ArticleStat'

export default function Details({ product }: { product: MathEssentialProductAPIStat }): React.ReactElement {
  const { t } = useTranslation('statistics')
  const locale = product.supportsCurrentLocale ? undefined : AppLanguage.EN
  const router = useRouter()
  const isFamilyView = router.asPath.includes(ROUTES.FAMILY_ADMIN_PANEL)
  const { totalQuizesCount, completedQuizesCount, totalArticlesCount, completedArticlesCount, activityStats } =
    product.details
  const totalActivity = activityStats?.length
  const progressBarData = getVideoProgressData(completedArticlesCount, totalArticlesCount)

  const btnText = product.summary.progressPercentage === 100 ? t('review') : t('continue')

  const onClickContinueBtn = (): void => {
    statisticsAnalytics.logButtonClick(btnText)
  }

  return (
    <div>
      <div className="flex space-x-4 my-4 pt-2">
        <ActivitySummary completedQuizesCount={completedQuizesCount} totalQuizesCount={totalQuizesCount} />

        <div className="w-full grow font-semibold rounded overflow-hidden shadow-card space-y-3 py-2">
          <p className="text-center">
            <span className="text-purple">{t('articles')}</span>
          </p>

          <PieProgress
            imgAltText={t('alt_texts.book')}
            imgSrcKey="scoreboard/book-white.svg"
            progressBarData={progressBarData}
            totalSlices={totalArticlesCount}
          />

          <p className="text-center">
            {completedArticlesCount}/{totalArticlesCount}
          </p>
        </div>
      </div>

      <div className="relative">
        <div className="w-full pt-2">
          {activityStats.map((activity: MathEssentialActivityStat, index) => (
            <>
              {activity.type === 'article' && <ArticleStat activity={activity} key={`article-${activity.id}`} />}
              {activity.type === 'quiz' && (
                <QuizStat activity={activity} hideBorder={totalActivity === index + 1} key={`quiz-${activity.id}`} />
              )}
            </>
          ))}
        </div>

        {!isFamilyView && (
          <ButtonPrimary
            className="ml-auto mt-4 px-2"
            href={product.continueActivity}
            locale={locale}
            noPadding
            onClick={onClickContinueBtn}
            size="medium"
          >
            {btnText}
          </ButtonPrimary>
        )}
      </div>
    </div>
  )
}
